import { useMemo } from 'react';
import {
  Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useThemeStore } from '@/store/theme';
import { useThemeColors } from '@/hooks/useThemeColors';
import {
  derive, metricValue, type Bucket, type DailyPoint, type MetricKey,
} from '@/api/tracker-dashboard';
import { fmtCompact, fmtInt, fmtMoney, fmtPct, paint, shortDay } from './format';

const METRICS: { key: MetricKey; label: string }[] = [
  { key: 'clicks', label: 'Clicks' },
  { key: 'uniq_clicks', label: 'Unique' },
  { key: 'regs', label: 'Regs' },
  { key: 'deps', label: 'Deps' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'cr_dep', label: 'CR dep' },
  { key: 'epc', label: 'EPC' },
];

function formatMetric(key: MetricKey, v: number): string {
  if (key === 'revenue') return fmtMoney(v);
  if (key === 'cr_dep') return fmtPct(v);
  if (key === 'epc') return `$${v.toFixed(3)}`;
  return fmtInt(v);
}

interface Props {
  daily: DailyPoint[];
  prevDaily: DailyPoint[];
  metric: MetricKey;
  onMetric: (m: MetricKey) => void;
  compare: boolean;
  onCompare: (v: boolean) => void;
}

/**
 * One metric at a time on one axis. A second scale on the right would let any
 * two series be drawn as crossing or diverging purely by choice of range, so
 * the comparison offered instead is the same metric over the preceding period —
 * same units, same axis, honest by construction.
 */
export function TrackerTrend({ daily, prevDaily, metric, onMetric, compare, onCompare }: Props) {
  const theme = useThemeStore((s) => s.theme);
  const themeColors = useThemeColors();
  const color = paint(metric, theme);
  const ghost = themeColors.axis;

  const data = useMemo(() => daily.map((d, i) => {
    const p = prevDaily[i];
    return {
      date: shortDay(d.date),
      iso: d.date,
      prevIso: p?.date ?? null,
      value: metricValue(d, metric),
      prev: p ? metricValue(p, metric) : null,
    };
  }), [daily, prevDaily, metric]);

  const tickFmt = (v: number) => {
    if (metric === 'revenue') return `$${fmtCompact(v)}`;
    if (metric === 'cr_dep') return `${v.toFixed(1)}%`;
    if (metric === 'epc') return `$${v.toFixed(2)}`;
    return fmtCompact(v);
  };

  const TrendTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload as typeof data[number];
    return (
      <div className="bg-surface/95 backdrop-blur border border-border rounded-xl shadow-lg p-3 text-sm">
        <div className="text-xs font-medium text-content-subtle mb-1.5">{shortDay(p.iso)}</div>
        <div className="flex items-center gap-2 text-content">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
          <span className="text-content-muted">This period</span>
          <span className="font-semibold font-mono ml-auto">{formatMetric(metric, p.value)}</span>
        </div>
        {compare && p.prev !== null && (
          <div className="flex items-center gap-2 mt-1 text-content">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ghost }} />
            <span className="text-content-muted">{p.prevIso ? shortDay(p.prevIso) : 'Previous'}</span>
            <span className="font-semibold font-mono ml-auto">{formatMetric(metric, p.prev)}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-content">
          {METRICS.find((m) => m.key === metric)?.label} by day
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex bg-surface-muted rounded-lg p-0.5 gap-0.5">
            {METRICS.map((m) => (
              <button
                key={m.key}
                onClick={() => onMetric(m.key)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                  metric === m.key ? 'bg-surface text-content shadow-sm' : 'text-content-subtle hover:text-content-muted'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-content-muted cursor-pointer select-none">
            <input
              type="checkbox" checked={compare} onChange={(e) => onCompare(e.target.checked)}
              className="rounded border-border"
            />
            Compare
          </label>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.22} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={themeColors.grid} vertical={false} />
          <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: themeColors.axis }} minTickGap={16} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: themeColors.axis }} tickFormatter={tickFmt} width={54} />
          <Tooltip content={<TrendTooltip />} cursor={{ stroke: themeColors.grid }} />
          {compare && (
            <Line
              type="monotone" dataKey="prev" stroke={ghost} strokeWidth={2} strokeDasharray="4 4"
              dot={false} isAnimationActive={false} connectNulls
            />
          )}
          <Area
            type="monotone" dataKey="value" stroke={color} strokeWidth={2}
            fill="url(#trendFill)" isAnimationActive={false}
            dot={data.length <= 14 ? { r: 3, fill: color, strokeWidth: 0 } : false}
            activeDot={{ r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * The four funnel steps. Bars are drawn as a share of clicks, which makes the
 * later steps very short — that is the truth of this funnel, so the step-to-step
 * conversion is printed next to each bar rather than the geometry being rescaled
 * to look healthier than it is.
 */
export function TrackerFunnel({ totals }: { totals: Bucket }) {
  const theme = useThemeStore((s) => s.theme);
  const d = derive(totals);
  const steps = [
    { label: 'Clicks', value: totals.clicks, step: null as number | null, color: paint('clicks', theme) },
    { label: 'Unique', value: totals.uniq_clicks, step: d.uniq_rate, color: paint('uniq_clicks', theme) },
    { label: 'Registrations', value: totals.regs, step: d.cr_reg, color: paint('regs', theme) },
    { label: 'Deposits', value: totals.deps, step: d.cr_reg_dep, color: paint('deps', theme) },
  ];
  const base = totals.clicks || 1;

  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5">
      <h3 className="text-sm font-semibold text-content mb-1">Funnel</h3>
      <p className="text-xs text-content-subtle mb-4">Each step as a share of the one above it</p>
      <div className="space-y-3">
        {steps.map((s, i) => (
          <div key={s.label}>
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <span className="text-xs font-medium text-content-muted">{s.label}</span>
              <span className="text-sm font-semibold font-mono text-content">{fmtInt(s.value)}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-surface-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    // A step that rounds to zero width would read as "no data"
                    // rather than "very few" — floor it at a hairline instead.
                    width: `${Math.max(s.value > 0 ? 1.5 : 0, (s.value / base) * 100)}%`,
                    backgroundColor: s.color,
                  }}
                />
              </div>
              <span className="text-[11px] font-mono text-content-subtle w-14 text-right shrink-0">
                {i === 0 ? '100%' : fmtPct(s.step ?? 0, 2)}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-border grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-content-subtle">Revenue</div>
          <div className="font-mono font-semibold text-content text-sm mt-0.5">{fmtMoney(d.revenue)}</div>
        </div>
        <div>
          <div className="text-content-subtle">Per deposit</div>
          <div className="font-mono font-semibold text-content text-sm mt-0.5">
            {totals.deps > 0 ? fmtMoney(d.per_dep) : '—'}
          </div>
        </div>
      </div>
    </div>
  );
}
